import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { NavLink, useNavigate } from 'react-router-dom';
import { Badge, Button, Icon, IconButton, Menu, Popover, Tooltip } from '@economic/gaudi';
import { t, tBB } from '../../../shared/components/l10n/l10n';
import { getTimeAgo } from '../../../shared/util/utilities/date';
import {
    getNotificationText,
    notificationDetails,
    Notification,
    getNotificationClickHandler,
} from '../../../shared/util/utilities/notifications';
import EmptyStateImage from './emptyState.svg';
import track from '../../../shared/util/utilities/mixpanel';
import './style.css';

type Props = {
    hide: () => void;
    notifications?: Notification[];
    onOpened: () => void;
    onMarkAsRead: (...notifications: Notification[]) => void;
};

const getUniqueNotificationGroups = (ungroupedNotifications: Notification[]) => {
    const notifications: Notification[] = [];
    const groupTotals = {};
    let hasUnread = false;

    ungroupedNotifications.forEach(notification => {
        if (notification.groupId) {
            if (groupTotals[notification.groupId]) {
                groupTotals[notification.groupId]++;
            } else {
                notifications.push(notification);
                groupTotals[notification.groupId] = 1;
            }
        } else {
            notifications.push(notification);
        }

        if (!notification.read) {
            hasUnread = true;
        }
    });

    return {
        notifications,
        groupTotals,
        hasUnread,
    };
};

const SingleNotification = props => {
    const { onHide: hide, groupTotal, onMarkAsRead: handleMarkAsRead, ...notification } = props;
    const navigate = useNavigate();
    const details = notificationDetails[notification.type];

    const text = getNotificationText(notification, groupTotal);
    const [hovering, setHovering] = useState(false);

    if (!text) {
        return null;
    }

    const handleClick = getNotificationClickHandler(notification, navigate);

    return (
        <div
            key={notification.id}
            className={cn('notifications__panel__feed__notification', {
                'notifications__panel__feed__notification--action': !!handleClick,
                'notifications__panel__feed__notification--unread': !notification.read,
            })}
            onMouseEnter={e => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            onClick={
                handleClick
                    ? () => {
                          track('notifications.panel', 'clicked-action', { notificationType: notification.type });
                          handleClick();
                          if (!notification.read) {
                              handleMarkAsRead(notification);
                          }
                          hide();
                      }
                    : undefined
            }>
            <div className="notifications__panel__feed__notification__icon">
                <Icon name={details.icon} />
            </div>
            <div className="notifications__panel__feed__notification__content ">
                <p>{text}</p>
                <p>{getTimeAgo(notification.createdAt)}</p>
            </div>
            <div className="notifications__panel__feed__notification__actions" onClick={e => e.stopPropagation()}>
                {!notification.read && (
                    <Tooltip title={t`NOTIFICATIONS_PANEL_NOTIFICATION_MARK_AS_READ`}>
                        <span>
                            {hovering ? (
                                <IconButton
                                    appearance="discrete"
                                    icon="ellipsis-horizontal"
                                    menu={menuProps => (
                                        <Menu {...menuProps}>
                                            <Menu.Item
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    handleMarkAsRead(notification);
                                                }}>
                                                {t`NOTIFICATIONS_PANEL_NOTIFICATION_MARK_AS_READ`}
                                            </Menu.Item>
                                        </Menu>
                                    )}
                                />
                            ) : (
                                <Badge aria-label="Unread notification" compact state="information">
                                    {t`NOTIFICATIONS_PANEL_NOTIFICATION_UNREAD`}
                                </Badge>
                            )}
                        </span>
                    </Tooltip>
                )}
            </div>
        </div>
    );
};

const EmptyState = ({ onHide: hide }) => (
    <div className="notifications__panel__empty">
        <img src={EmptyStateImage} alt={t`NOTIFICATIONS_PANEL_EMPTY_HEADLINE`} />
        <h3>{t`NOTIFICATIONS_PANEL_EMPTY_HEADLINE`}</h3>
        <p>
            {tBB(`NOTIFICATIONS_PANEL_EMPTY_DESCRIPTION`, {
                __LINK: content => (
                    <NavLink
                        to="/user/notifications/settings"
                        onClick={() => {
                            track('notifications.intro', 'description-link-clicked');
                            hide();
                        }}>
                        {content}
                    </NavLink>
                ),
            })}
        </p>
        <p>
            <a
                href={t`NOTIFICATIONS_PANEL_EMPTY_MORE_INFO_URL`}
                onClick={() => {
                    track('notifications.intro', 'read-more-clicked');
                    hide();
                }}
                rel="noopener noreferrer"
                target="_blank">
                {t`NOTIFICATIONS_PANEL_EMPTY_MORE_INFO`}
            </a>
        </p>
    </div>
);

const Panel = ({ hide, notifications: ungroupedNotifications = [], onOpened: handleOpened, onMarkAsRead }: Props) => {
    const navigate = useNavigate();
    const { notifications, groupTotals, hasUnread } = React.useMemo(() => getUniqueNotificationGroups(ungroupedNotifications), [
        ungroupedNotifications,
    ]);

    React.useEffect(() => {
        track('notifications.panel', 'opened');
        handleOpened();
    }, []);

    return (
        <div className="notifications__panel">
            <div className="notifications__panel__header">
                <span>{t`NOTIFICATIONS_PANEL_HEADLINE`}</span>
                <Button
                    aria-label={t`NOTIFICATIONS_PANEL_MARK_ALL_READ_BUTTON`}
                    appearance="discrete"
                    disabled={!hasUnread}
                    onClick={() => {
                        track('notifications.panel', 'mark-all-read-clicked');
                        onMarkAsRead(...notifications);
                    }}>
                    {t`NOTIFICATIONS_PANEL_MARK_ALL_READ_BUTTON`}
                </Button>
                <Tooltip title={t`NOTIFICATIONS_PANEL_SETTINGS_BUTTON`}>
                    <span>
                        <IconButton
                            aria-label={t`NOTIFICATIONS_PANEL_SETTINGS_BUTTON`}
                            appearance="discrete"
                            icon="settings"
                            onClick={event => {
                                navigate('/user/notifications/settings');
                                hide();
                            }}
                            style={{ marginRight: '-7px' }}
                        />
                    </span>
                </Tooltip>
            </div>

            <div
                className={cn('notifications__panel__feed', {
                    notifications__panel__feed__empty: !notifications?.length,
                })}>
                {notifications?.length ? (
                    notifications.map((notification: Notification) => (
                        <SingleNotification
                            {...notification}
                            key={notification.id}
                            groupTotal={notification.groupId ? groupTotals[notification.groupId] : undefined}
                            onHide={hide}
                            onMarkAsRead={onMarkAsRead}
                        />
                    ))
                ) : (
                    <EmptyState onHide={hide} />
                )}
            </div>

            {notifications?.length ? (
                <div className="notifications__panel__footer">
                    <a
                        href="/user/notifications"
                        onClick={(event: any) => {
                            event.preventDefault();
                            navigate('/user/notifications');
                            hide();
                        }}>
                        {t`NOTIFICATIONS_PANEL_SEE_ALL_BUTTON`}
                    </a>
                </div>
            ) : null}
        </div>
    );
};

const PopoverPanel = ({ notifications, onOpened, hide, onMarkAsRead }) => {
    const Econ = window.top?.firebird.Econ;
    useEffect(() => {
        for (const frame in Econ.frames) {
            Econ.frames[frame].addEventListener('click', hide);
        }

        return () => {
            for (const frame in Econ.frames) {
                Econ.frames[frame].removeEventListener('click', hide);
            }
        };
    }, [Econ.frames]);

    return <Panel notifications={notifications} onOpened={onOpened} hide={hide} onMarkAsRead={onMarkAsRead} />;
};

export default function NotificationsPanel({ notifications, onOpened, onMarkAsRead, ...props }: any) {
    return (
        <Popover {...props} aria-label="Notifications" style={{ marginTop: '-3px', padding: 0, width: '375px' }} tabIndex={0}>
            {({ hide }) => (
                <PopoverPanel notifications={notifications} onOpened={onOpened} hide={hide} onMarkAsRead={onMarkAsRead} />
            )}
        </Popover>
    );
}
